import { useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ProgressReport } from "../components";
import { useAuth } from "../contexts";
import { PageLoader, BackButton, Button } from "../components";
import type { ReportType } from "../hooks/useProgressReport";

/**
 * ProgressReportPage - View detailed progress reports for a child.
 *
 * URL: /parent/reports/:childId
 * Query params: ?type=weekly|monthly
 *
 * Features:
 * - Weekly and monthly report views
 * - Print and export functionality
 * - Period comparison
 * - Detailed discipline breakdown
 */
export function ProgressReportPage() {
  const { childId } = useParams<{ childId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  // Get report type from query params, default to weekly
  const reportTypeParam = searchParams.get("type");
  const initialReportType: ReportType =
    reportTypeParam === "monthly" ? "monthly" : "weekly";

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Loading state
  if (authLoading) {
    return <PageLoader message="Loading..." />;
  }

  // No child ID
  if (!childId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12">
        <div className="text-center max-w-md">
          <span className="text-6xl mb-4 block">🤔</span>
          <h1 className="font-display font-bold text-display-md text-neutral-800 mb-2">
            No child selected
          </h1>
          <p className="font-body text-body-lg text-neutral-600 mb-6">
            Please select a child from the parent dashboard to view their report.
          </p>
          <Button variant="primary" onClick={() => navigate("/parent")}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-magic-50/10 print:bg-white">
      {/* Back button - hidden in print */}
      <div className="fixed top-4 left-4 z-50 print:hidden">
        <BackButton to="/parent" />
      </div>

      {/* Report */}
      <ProgressReport
        childId={childId}
        initialReportType={initialReportType}
      />
    </div>
  );
}

export default ProgressReportPage;
